import React from 'react';
import { Drawer, Table, Button, Typography, Row, Divider, InputNumber, Space, Input, Select, Col, Radio, Card, Statistic } from 'antd';
import { DeleteOutlined, EuroOutlined, PlusOutlined, CheckCircleOutlined, ArrowLeftOutlined, WalletOutlined, CreditCardOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../utils/currency';

const { Title, Text } = Typography;

const CartDrawer = ({
    visible,
    onClose,
    cart,
    onUpdateQty,
    onRemoveItem,
    onCheckout,
    loading,
    adjustments,
    onAddAdjustment,
    onRemoveAdjustment,
    checkoutMode,
    onBackToCart,
    paymentMethod,
    setPaymentMethod,
    onFinish
}) => {
    const [adjLabel, setAdjLabel] = React.useState('');
    const [adjAmount, setAdjAmount] = React.useState(0);
    const [adjKind, setAdjKind] = React.useState('DISCOUNT'); // DISCOUNT | FEE

    // Calculations
    const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);
    const totalHT = cart.reduce((sum, item) => sum + ((item.price / (1 + item.vat_rate / 100)) * item.qty), 0);
    const totalVAT = cartTotal - totalHT;

    // Adjustments Calc
    const discountTotal = adjustments?.filter(a => a.kind === 'DISCOUNT').reduce((sum, a) => sum + a.amount, 0) || 0;
    const feeTotal = adjustments?.filter(a => a.kind === 'FEE').reduce((sum, a) => sum + a.amount, 0) || 0;
    const finalTotal = Math.max(0, cartTotal - discountTotal + feeTotal);

    const handleAddAdj = () => {
        if (!adjLabel || adjAmount <= 0) return;
        onAddAdjustment({
            id: Date.now(),
            label: adjLabel,
            amount: adjAmount,
            kind: adjKind
        });
        setAdjLabel('');
        setAdjAmount(0);
    };

    const columns = [
        {
            title: 'Product',
            dataIndex: 'label',
            key: 'label',
            render: (text) => <Text ellipsis style={{ width: 140 }}>{text}</Text>
        },
        {
            title: 'Qty',
            dataIndex: 'qty',
            key: 'qty',
            width: 80,
            render: (val, record) => (
                <InputNumber
                    min={1}
                    max={record.type === 0 ? record.stock : 999}
                    value={val}
                    size="small"
                    style={{ width: 50 }}
                    onChange={(v) => onUpdateQty(record.id, v)}
                />
            )
        },
        {
            title: 'Total',
            key: 'total',
            align: 'right',
            render: (_, rec) => (rec.price * rec.qty).toFixed(3)
        },
        {
            title: '',
            key: 'action',
            width: 40,
            render: (_, record) => (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onRemoveItem(record.id)} />
            )
        }
    ];

    return (
        <Drawer
            title={
                checkoutMode ? (
                    <Row align="middle">
                        <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBackToCart} style={{ marginRight: 8 }} />
                        <Title level={4} style={{ margin: 0 }}>Payment</Title>
                    </Row>
                ) : (
                    <Row justify="space-between" align="middle">
                        <span>Current Cart ({cart.reduce((sum, i) => sum + i.qty, 0)})</span>
                        <Title level={4} style={{ margin: 0, color: '#52c41a' }}>{formatCurrency(finalTotal)}</Title>
                    </Row>
                )
            }
            placement="right"
            onClose={onClose}
            open={visible}
            width={480}
            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column' }}
        >
            {checkoutMode ? (
                // Checkout Mode UI
                <div style={{ padding: '24px', flex: 1 }}>
                    <Card bordered={false} style={{ marginBottom: 24, textAlign: 'center' }}>
                        <Statistic title="Total Payable Amount" value={finalTotal} precision={3} suffix="JOD" valueStyle={{ color: '#52c41a' }} />
                    </Card>

                    <Card title="Payment Method" bordered={false}>
                        <Radio.Group
                            value={paymentMethod}
                            onChange={e => setPaymentMethod(e.target.value)}
                            style={{ width: '100%' }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Radio.Button value={4} style={{ width: '100%', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                                    <Space><WalletOutlined /> Cash</Space>
                                </Radio.Button>
                                <Radio.Button value={6} style={{ width: '100%', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', marginTop: 12 }}>
                                    <Space><CheckCircleOutlined /> CliQ</Space>
                                </Radio.Button>
                                {/* New Visa Option */}
                                <Radio.Button value={8} style={{ width: '100%', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', marginTop: 12 }}>
                                    <Space><CreditCardOutlined /> Visa</Space>
                                </Radio.Button>
                            </Space>
                        </Radio.Group>
                    </Card>

                    <Button
                        type="primary"
                        size="large"
                        block
                        icon={<EuroOutlined />}
                        onClick={onFinish}
                        loading={loading}
                        style={{ height: '60px', fontSize: '20px', marginTop: 40 }}
                    >
                        Finish & Print
                    </Button>
                </div>
            ) : (
                // Cart Mode UI
                <>
                    <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
                        <Table
                            dataSource={cart}
                            columns={columns}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            locale={{ emptyText: <div style={{ padding: 40, textAlign: 'center' }}>Cart is empty</div> }}
                            style={{ background: 'transparent' }}
                        />

                        {/* Adjustments Section */}
                        {cart.length > 0 && (
                            <div style={{ marginTop: 24, padding: '16px', borderRadius: 8, border: '1px solid rgba(140, 140, 140, 0.2)' }}>
                                <Text strong>Adjustments</Text>
                                <Row gutter={8} style={{ marginTop: 8 }}>
                                    <Col span={8}>
                                        <Input placeholder="Label" value={adjLabel} onChange={e => setAdjLabel(e.target.value)} size="small" />
                                    </Col>
                                    <Col span={6}>
                                        <Select value={adjKind} onChange={setAdjKind} size="small" style={{ width: '100%' }}>
                                            <Select.Option value="DISCOUNT">Disc</Select.Option>
                                            <Select.Option value="FEE">Fee</Select.Option>
                                        </Select>
                                    </Col>
                                    <Col span={6}>
                                        <InputNumber
                                            min={0}
                                            value={adjAmount}
                                            onChange={setAdjAmount}
                                            size="small"
                                            style={{ width: '100%' }}
                                            placeholder="0.000"
                                            precision={3}
                                        />
                                    </Col>
                                    <Col span={4}>
                                        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={handleAddAdj} block />
                                    </Col>
                                </Row>

                                {adjustments?.length > 0 && (
                                    <div style={{ marginTop: 12 }}>
                                        {adjustments.map((adj, idx) => (
                                            <Row key={idx} justify="space-between" align="middle" style={{ marginBottom: 4, fontSize: '12px' }}>
                                                <Text type="secondary">{adj.label} ({adj.kind === 'DISCOUNT' ? '-' : '+'})</Text>
                                                <Space>
                                                    <Text type={adj.kind === 'DISCOUNT' ? 'success' : 'danger'}>
                                                        {adj.kind === 'DISCOUNT' ? '-' : '+'}{formatCurrency(adj.amount)}
                                                    </Text>
                                                    <DeleteOutlined onClick={() => onRemoveAdjustment(idx)} style={{ cursor: 'pointer', color: '#ff4d4f' }} />
                                                </Space>
                                            </Row>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div style={{ padding: '24px', borderTop: '1px solid rgba(140, 140, 140, 0.2)' }}>
                        <Row justify="space-between" style={{ marginBottom: 4 }}>
                            <Text type="secondary">Subtotal (HT):</Text>
                            <Text>{formatCurrency(totalHT)}</Text>
                        </Row>
                        <Row justify="space-between" style={{ marginBottom: 4 }}>
                            <Text type="secondary">VAT (16%):</Text>
                            <Text>{formatCurrency(totalVAT)}</Text>
                        </Row>
                        {discountTotal > 0 && (
                            <Row justify="space-between" style={{ marginBottom: 4 }}>
                                <Text type="secondary">Discount:</Text>
                                <Text type="success">-{formatCurrency(discountTotal)}</Text>
                            </Row>
                        )}
                        {feeTotal > 0 && (
                            <Row justify="space-between" style={{ marginBottom: 12 }}>
                                <Text type="secondary">Fees:</Text>
                                <Text type="danger">+{formatCurrency(feeTotal)}</Text>
                            </Row>
                        )}
                        <Divider style={{ margin: '8px 0' }} />
                        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                            <Title level={4} style={{ margin: 0 }}>Total:</Title>
                            <Title level={3} style={{ margin: 0, color: '#52c41a' }}>{formatCurrency(finalTotal)}</Title>
                        </Row>

                        <Button
                            type="primary"
                            size="large"
                            block
                            icon={<EuroOutlined />}
                            onClick={onCheckout}
                            style={{ height: 50, fontSize: 18 }}
                            disabled={cart.length === 0}
                            loading={loading}
                        >
                            Process Checkout
                        </Button>
                    </div>
                </>
            )}
        </Drawer>
    );
};

export default CartDrawer;
